import { buildCollection, buildProperty, EntityAction } from "@firecms/core";

import { buildConnectLinkUrl, copyConnectLink } from "./apple_connect_link.utils";
import {
  CONNECT_LINK_GENERATE_ROLES,
  GRANT_STATUS_VIEW_ROLES,
  hasAnyRole,
} from "./apple_grant_status.roles";
import {
  APPLE_GRANT_STATUS_PATH,
  AppleGrantStatusEntity,
  describeLinkedCompany,
  friendlyGrantStatus,
  GRANT_STATE_LABELS,
  isConnectLinkBlocked,
  REVIEW_REASON_LABELS,
} from "./apple_grant_status.types";
import { AppleGrantStatusChip } from "./AppleGrantStatusChip";
import { AppleInsightsView } from "./AppleInsightsView";
import { GenerateConnectLinkAction } from "./GenerateConnectLinkAction";

/**
 * Row action: re-copy the connect link for a client whose grant has lapsed.
 *
 * Strictly safer than the dialog, because both IDs come from the document rather than a human — the
 * typo risk that the dialog's confirm step exists to mitigate is structurally absent here. So there
 * is no confirm step: the guard is a pure check on values already loaded.
 *
 * ⚠️ `EntityAction` in FireCMS 3.0.0-beta.14 has no `isEnabled`/`disabled` predicate (only `name`,
 * `key`, `icon`, `onClick`, `collapsed`, `includeInForm`), so this action cannot be hidden on ACTIVE
 * rows declaratively. The block therefore runs inside `onClick` and explains itself, rather than the
 * action being absent.
 */
const copyConnectLinkAction: EntityAction<Partial<AppleGrantStatusEntity>> = {
  name: "Copy connect link",
  key: "copy_connect_link",
  onClick: async ({ entity, context }) => {
    const { snackbarController, authController } = context;

    if (!hasAnyRole(authController, CONNECT_LINK_GENERATE_ROLES)) {
      snackbarController.open({
        type: "error",
        message: "You do not have permission to generate Apple connect links.",
      });
      return;
    }

    if (isConnectLinkBlocked(entity.values)) {
      snackbarController.open({
        type: "warning",
        message: entity.values.needsReview
          ? "This account is flagged for review — more than one Apple company holds an active link. Ask an engineer to resolve it before sending another link."
          : `${describeLinkedCompany(entity.values, "This client")} is already linked. They don't need a new link.`,
      });
      return;
    }

    const accountId = entity.values.accountId ?? entity.id;
    const salesforceId = entity.values.salesforce_id;
    if (!accountId || !salesforceId) {
      // The projection allows a null `salesforce_id`, and the OAuth route rejects a request without
      // one. Say so rather than handing over a link that 400s in the client's browser.
      snackbarController.open({
        type: "error",
        message:
          "This record has no Salesforce ID, which the connect link requires. Use “Generate connect link” and enter both IDs.",
      });
      return;
    }

    // `buildConnectLinkUrl` throws when VITE_ABC_OAUTH_BASE_URL is missing. Unhandled, that would
    // make the menu item look inert; report it instead.
    let url: string;
    try {
      url = buildConnectLinkUrl({ accountId, salesforceId });
    } catch (e) {
      snackbarController.open({
        type: "error",
        message: e instanceof Error ? e.message : "Could not build the connect link.",
      });
      return;
    }

    const copied = await copyConnectLink(url);
    snackbarController.open(
      copied
        ? { type: "success", message: "Connect link copied. Send it to the client." }
        : { type: "error", message: "Could not copy the link to the clipboard." },
    );
  },
};

/**
 * Read-only view of `apple_grant_status` — the backend's secret-free projection of `apple_org_grants`
 * — listing every client who has linked their Apple Business Connect account.
 *
 * Two things to know before changing this:
 *
 * 1. **This collection is empty until a client completes a grant.** The backend writes the
 *    projection on OAuth callback, so a client who has been sent a link but has not clicked it does
 *    NOT appear here. That is why link generation is a toolbar action rather than something you do
 *    from a row.
 * 2. **`apple_org_grants` must never be registered in this CMS.** It holds the AES-256-GCM-encrypted
 *    OAuth access and refresh tokens and is default-denied by the Firestore rules. This projection
 *    exists precisely so that collection stays unreadable from here.
 *
 * Every property is `readOnly` and no write permission is granted: the documents are backend-owned
 * and the CMS holds only `allow read` on them, so an edit button would always fail.
 */
export const appleGrantStatusCollection = buildCollection<Partial<AppleGrantStatusEntity>>({
  id: APPLE_GRANT_STATUS_PATH,
  path: APPLE_GRANT_STATUS_PATH,
  name: "Apple Business Connect",
  singularName: "Apple Grant",
  description:
    "Clients who have linked their Apple Business Connect account. Use “Generate connect link” for a client who has not linked yet.",
  group: "Partners",
  textSearchEnabled: false,
  icon: "link",
  permissions: ({ authController }) => ({
    read: hasAnyRole(authController, GRANT_STATUS_VIEW_ROLES),
    edit: false,
    create: false,
    delete: false,
  }),
  Actions: GenerateConnectLinkAction,
  entityActions: [copyConnectLinkAction],
  // The read-only Apple Insights panel. Registered here because this collection is the per-client
  // record that carries both the DearDoc `accountId` and the Apple link state the panel needs, and
  // the panel adds nothing writable — it writes no document at all. See `AppleInsightsView.tsx`.
  //
  // ⚠️ RETRACTED: this used to read "it reads Apple live" and "it is inert until its backend route is
  // registered, and says so on screen." Both are now false, and the second one described a route that
  // was never built and never will be. The panel does NOT read Apple, and there is no backend route:
  // a browser-callable route would have to carry the `x-internal-secret` that guards every
  // `/internal/*` endpoint — including `POST /internal/locations`, `/brands`, `/media` and
  // `/showcases` — and shipping that secret in a bundle anyone can read would open every internal
  // WRITE endpoint in order to serve one read. Instead a daily backend job writes the read-only,
  // secret-free `apple_insights_status` projection and this panel reads THAT with the Firebase ID
  // token the console already has.
  //
  // ⚠️ IT NEEDS A FIRESTORE READ RULE ON `apple_insights_status`, added by hand in the console. Rules
  // for this project are console-managed and shared across projects; there is no `firestore.rules` in
  // either repo and CI deploys none. Until that rule exists the collection is default-denied and the
  // panel renders a read error naming the collection — diagnosable, rather than looking like a client
  // with no engagement.
  entityViews: [
    {
      key: "apple_insights",
      name: "Insights",
      Builder: AppleInsightsView,
    },
  ],
  // Puts the status chip in the FIRST column — additional fields otherwise append after every
  // property, burying the one thing a staff member scans for.
  //
  // ⚠️ Must list EVERY key, not just the table columns. `getFormFieldKeys` filters the entity form
  // through this same array, so an omitted key disappears from the detail view too — which would
  // hide exactly the conflict-review fields the detail view exists for. Table trimming stays the
  // job of `hideFromCollection`, which `hideAndExpandKeys` honours even when driven by this array.
  propertiesOrder: [
    "linkStatus",
    "legalName",
    "createdAt",
    "accountId",
    "salesforce_id",
    "state",
    "companyId",
    "orgId",
    "lastRefreshedAt",
    "needsReview",
    "reviewReason",
    "activeCompanyCount",
    "conflictingCompanies",
    "conflictDetectedAt",
  ],
  properties: {
    legalName: buildProperty({
      name: "Company (Apple)",
      description:
        "Apple's own legal name for the linked company. This is the field that confirms the right client is linked.",
      readOnly: true,
      dataType: "string",
    }),
    createdAt: buildProperty({
      name: "Linked At",
      readOnly: true,
      dataType: "date",
    }),
    accountId: buildProperty({
      name: "DearDoc Account ID",
      description:
        "Also the document ID. Lives in the dashboard project — not verifiable from here.",
      readOnly: true,
      dataType: "string",
    }),
    salesforce_id: buildProperty({
      name: "Salesforce ID",
      readOnly: true,
      dataType: "string",
    }),
    // Covered in the collection table by the status chip below; kept here for the detail view.
    state: buildProperty({
      name: "Grant State",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
      enumValues: GRANT_STATE_LABELS,
    }),
    companyId: buildProperty({
      name: "Apple Company ID",
      description: "Resolved by the backend from Apple's /api/v1/me. Never entered by a human.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    orgId: buildProperty({
      name: "Apple Org ID",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    lastRefreshedAt: buildProperty({
      name: "Token Last Refreshed",
      readOnly: true,
      hideFromCollection: true,
      dataType: "date",
    }),
    needsReview: buildProperty({
      name: "Needs Review",
      description:
        "More than one Apple company holds an active link for this account. While this is set, the backend refuses every delegated write.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "boolean",
    }),
    reviewReason: buildProperty({
      name: "Review Reason",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
      enumValues: REVIEW_REASON_LABELS,
    }),
    activeCompanyCount: buildProperty({
      name: "Active Apple Companies",
      description:
        "Distinct Apple companies holding an active link. 1 is healthy; more is a conflict.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "number",
    }),
    conflictingCompanies: buildProperty({
      name: "Conflicting Companies",
      description:
        "The competing companies. The headline company above is the incumbent — the one that linked first.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          companyId: buildProperty({ name: "Apple Company ID", dataType: "string" }),
          legalName: buildProperty({ name: "Company (Apple)", dataType: "string" }),
          state: buildProperty({
            name: "Grant State",
            dataType: "string",
            enumValues: GRANT_STATE_LABELS,
          }),
          createdAt: buildProperty({ name: "Linked At", dataType: "date" }),
        },
      },
    }),
    conflictDetectedAt: buildProperty({
      name: "Conflict First Seen",
      readOnly: true,
      hideFromCollection: true,
      dataType: "date",
    }),
  },
  additionalFields: [
    {
      key: "linkStatus",
      name: "Status",
      dependencies: ["state", "needsReview", "activeCompanyCount"],
      Builder: AppleGrantStatusChip,
      value: ({ entity }) => friendlyGrantStatus(entity.values),
    },
  ],
});
